import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";

const FORM_ENDPOINT = "https://readdy.ai/api/form/db9m67hrh5tg6sock7g0";

const SERVICES = [
  {
    icon: "ri-film-line",
    title: "片库整理",
    desc: "按类型、地区、年份整理影片资料，每部影片都包含简介、评分、导演与主演信息，方便快速判断是否合口味。",
  },
  {
    icon: "ri-search-line",
    title: "快速检索",
    desc: "支持片名、导演、演员与类型的模糊搜索，搜索结果即时更新，并可按最新、热度、评分自由排序。",
  },
  {
    icon: "ri-heart-3-line",
    title: "本地收藏",
    desc: "喜欢的影片一键收藏，收藏记录保存在本机浏览器，不注册也能拥有自己的片单。",
  },
];

const STEPS = [
  {
    title: "选择类型",
    desc: "从首页的分类入口进入感兴趣的类型，或在分类页直接切换类型、地区与年份。",
  },
  {
    title: "筛选排序",
    desc: "用筛选条件缩小范围，再按最新上映、播放热度或评分排序，快速锁定目标影片。",
  },
  {
    title: "查看详情",
    desc: "点击影片卡片进入详情页，查看剧情简介、主创信息与同类型推荐。",
  },
  {
    title: "收藏片单",
    desc: "点击收藏按钮保存影片，随时在「我的收藏」中回到自己的待看清单。",
  },
];

export default function About() {
  useSeo({
    title: "关于我们 - 免费看大片在线观看平台",
    description:
      "免费看大片是面向影迷的影视资料与片单整理平台，提供最新热门电影、经典大片分类浏览、搜索与收藏服务，帮助用户快速找到想看的影片。",
    keywords: "关于免费看大片,电影平台介绍,在线观看服务,联系我们,免费看大片",
  });

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [feedback, setFeedback] = useState("");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const formData = new FormData(formElement);
    const honeypot = String(formData.get("contact_alt") || "").trim();

    if (honeypot) {
      setStatus("success");
      setFeedback("反馈已提交，感谢你的建议，我们会尽快查看。");
      return;
    }

    formData.delete("contact_alt");
    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string" && value.trim()) {
        payload.append(key, value);
      }
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const normalized = String(serverMessage || "").toLowerCase();
      const isSuccess =
        response.ok &&
        normalized.indexOf("spam") === -1 &&
        (parsed === null || parsed?.code === "OK" || parsed?.code === undefined);

      if (isSuccess) {
        setStatus("success");
        setFeedback("反馈已提交，感谢你的建议，我们会尽快查看。");
        formElement.reset();
      } else {
        setStatus("error");
        setFormError(
          parsed?.meta?.message ||
            parsed?.message ||
            parsed?.meta?.detail ||
            "提交失败，请稍后重试，或直接发送邮件联系我们。",
        );
      }
    } catch (error) {
      console.warn("表单提交失败", error);
      setStatus("error");
      setFormError("网络异常，提交没有成功，请稍后重试。");
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-8 md:px-8 md:py-10">
      <nav aria-label="面包屑导航" className="flex items-center gap-1.5 text-xs text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-300">
          首页
        </Link>
        <i className="ri-arrow-right-s-line text-foreground-600" />
        <span className="text-foreground-200">关于我们</span>
      </nav>

      <header className="mt-4 overflow-hidden rounded-lg border border-background-200/70 bg-gradient-to-r from-primary-950/70 via-background-100 to-background-100 p-6 md:p-10">
        <p className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-accent-400">
          <span className="h-3 w-0.5 rounded-full bg-accent-500" />
          About us
        </p>
        <h1 className="mt-3 font-heading text-3xl font-semibold text-foreground-50 md:text-4xl">
          关于免费看大片
        </h1>
        <p className="mt-4 max-w-3xl text-sm leading-7 text-foreground-300 md:text-base md:leading-8">
          免费看大片是一个面向影迷的影视资料与片单整理平台。我们把最新上映的热门电影与值得重温的经典大片放在一起，用清晰的分类、完整的影片资料和口碑榜单，帮你在几分钟内决定今晚要看什么。
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to="/category/全部"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-film-line text-base" />
            浏览全部影片
          </Link>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm text-foreground-200 transition-colors hover:border-accent-500/60 hover:text-accent-300"
          >
            <i className="ri-mail-line text-base" />
            联系我们
          </a>
        </div>
      </header>

      <section className="mt-10">
        <h2 className="font-heading text-xl font-semibold text-foreground-50 md:text-2xl">
          我们提供什么
        </h2>
        <div className="mt-5 grid gap-4 md:grid-cols-3">
          {SERVICES.map((service) => (
            <div
              key={service.title}
              className="rounded-lg border border-background-200/70 bg-background-100 p-5"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-background-200/70 text-accent-400">
                <i className={`${service.icon} text-xl`} />
              </span>
              <h3 className="mt-4 font-heading text-base font-medium text-foreground-50">
                {service.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-400">{service.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10 grid gap-6 overflow-hidden rounded-lg border border-background-200/70 bg-background-100 lg:grid-cols-2">
        <div className="h-64 w-full lg:h-full lg:min-h-[320px]">
          <img
            src="https://readdy.ai/api/search-image?query=Cinematic%20editorial%20photo%20of%20a%20cozy%20private%20screening%20room%20with%20rows%20of%20dark%20velvet%20seats%2C%20a%20glowing%20blank%20screen%20and%20warm%20amber%20wall%20lamps%2C%20deep%20navy%20walls%2C%20subtle%20haze%2C%20premium%20film%20club%20atmosphere%2C%20wide%20composition%2C%20no%20text%2C%20no%20people&width=1200&height=800&seq=about-01&orientation=landscape"
            alt="免费看大片影视片库与放映空间"
            title="免费看大片 影视片库与放映空间"
            className="h-full w-full object-cover object-center"
          />
        </div>
        <div className="p-5 md:p-8">
          <p className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-accent-400">
            <span className="h-3 w-0.5 rounded-full bg-accent-500" />
            Positioning
          </p>
          <h2 className="mt-2 font-heading text-xl font-semibold text-foreground-50 md:text-2xl">
            网站定位与服务说明
          </h2>
          <p className="mt-4 text-sm leading-7 text-foreground-400">
            我们的目标用户是喜欢在线看电影的普通观众与年轻影迷。相比复杂的推荐逻辑，我们更在意信息的完整与浏览的顺畅：每部影片都能看到海报、评分、类型、地区、年份、导演与主演，点开就能读到剧情简介和同类推荐。
          </p>
          <p className="mt-3 text-sm leading-7 text-foreground-400">
            需要说明的是，当前版本的播放区域为前端界面演示，暂不提供真实影片资源。所有影片信息均为虚构示例内容，仅用于展示平台功能与页面结构。
          </p>
          <ul className="mt-5 flex flex-col gap-2.5">
            {[
              "覆盖 12 个类型分区，持续补充最新上映片单",
              "完整的影片资料，信息前后一致、可交叉核对",
              "收藏与筛选均在浏览器本地完成，无需注册",
            ].map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm text-foreground-300">
                <i className="ri-check-double-line mt-0.5 text-primary-300" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section id="instructions" className="mt-10 scroll-mt-24">
        <h2 className="font-heading text-xl font-semibold text-foreground-50 md:text-2xl">
          使用说明
        </h2>
        <p className="mt-2 text-sm text-foreground-500">四步找到想看的影片，全程无需注册。</p>
        <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((step, index) => (
            <div
              key={step.title}
              className="rounded-lg border border-background-200/70 bg-background-100 p-5"
            >
              <span className="font-heading text-2xl font-semibold text-primary-300/70">
                0{index + 1}
              </span>
              <h3 className="mt-2 font-heading text-sm font-medium text-foreground-50">
                {step.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-400">{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="contact" className="mt-10 scroll-mt-24">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
          <div className="rounded-lg border border-background-200/70 bg-background-100 p-5 md:p-6">
            <p className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-accent-400">
              <span className="h-3 w-0.5 rounded-full bg-accent-500" />
              Contact
            </p>
            <h2 className="mt-2 font-heading text-xl font-semibold text-foreground-50 md:text-2xl">
              联系我们
            </h2>
            <p className="mt-3 text-sm leading-7 text-foreground-400">
              对影片资料、页面功能或内容授权有疑问，欢迎通过邮件或右侧表单告诉我们。我们通常会在两个工作日内回复。
            </p>

            <div className="mt-5 flex flex-col gap-3">
              <div className="flex items-center gap-3 rounded-md bg-background-200/50 p-3.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-50 text-primary-300">
                  <i className="ri-mail-line text-base" />
                </span>
                <div>
                  <p className="text-xs text-foreground-500">联系邮箱</p>
                  <p className="mt-0.5 text-sm text-foreground-100">
                    service@mianfeikandapian.com
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-md bg-background-200/50 p-3.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-50 text-primary-300">
                  <i className="ri-time-line text-base" />
                </span>
                <div>
                  <p className="text-xs text-foreground-500">回复时间</p>
                  <p className="mt-0.5 text-sm text-foreground-100">工作日 10:00 - 18:00</p>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-md bg-background-200/50 p-3.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-50 text-primary-300">
                  <i className="ri-map-pin-line text-base" />
                </span>
                <div>
                  <p className="text-xs text-foreground-500">运营团队</p>
                  <p className="mt-0.5 text-sm text-foreground-100">免费看大片内容运营组</p>
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-background-200/70 bg-background-100 p-5 md:p-6">
            <h2 className="font-heading text-lg font-semibold text-foreground-50">
              意见反馈
            </h2>
            <p className="mt-1.5 text-sm text-foreground-500">
              填写下面的表单，我们会认真读完每一条建议。
            </p>

            {status === "success" ? (
              <div className="mt-5 rounded-lg border border-primary-500/40 bg-primary-500/10 p-5 text-center">
                <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-primary-500 text-background-50">
                  <i className="ri-check-line text-xl" />
                </span>
                <p className="mt-3 font-heading text-base text-foreground-50">提交成功</p>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-300">{feedback}</p>
                <button
                  type="button"
                  onClick={() => {
                    setStatus("idle");
                    setFeedback("");
                  }}
                  className="mt-4 cursor-pointer rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-200 transition-colors hover:border-primary-500/60 hover:text-primary-300"
                >
                  再提交一条
                </button>
              </div>
            ) : (
              <form
                id="feedback-form"
                data-readdy-form
                action={FORM_ENDPOINT}
                method="post"
                onSubmit={handleSubmit}
                className="mt-5 flex flex-col gap-4"
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="feedback-name"
                      className="mb-1.5 block text-xs text-foreground-400"
                    >
                      称呼
                    </label>
                    <input
                      id="feedback-name"
                      name="name"
                      type="text"
                      required
                      placeholder="怎么称呼你"
                      className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-100 outline-none transition-colors placeholder:text-foreground-600 focus:border-primary-500/70"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="feedback-email"
                      className="mb-1.5 block text-xs text-foreground-400"
                    >
                      邮箱
                    </label>
                    <input
                      id="feedback-email"
                      name="email"
                      type="email"
                      required
                      placeholder="用于接收回复"
                      className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-100 outline-none transition-colors placeholder:text-foreground-600 focus:border-primary-500/70"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="feedback-topic"
                    className="mb-1.5 block text-xs text-foreground-400"
                  >
                    反馈类型
                  </label>
                  <div className="relative">
                    <select
                      id="feedback-topic"
                      name="topic"
                      defaultValue="内容建议"
                      className="w-full cursor-pointer appearance-none rounded-md border border-background-200 bg-background-50 px-3 py-2.5 pr-9 text-sm text-foreground-100 outline-none transition-colors focus:border-primary-500/70"
                    >
                      <option value="内容建议">内容建议</option>
                      <option value="功能问题">功能问题</option>
                      <option value="内容授权">内容授权</option>
                      <option value="合作洽谈">合作洽谈</option>
                    </select>
                    <i className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-base text-foreground-500" />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="feedback-message"
                    className="mb-1.5 block text-xs text-foreground-400"
                  >
                    反馈内容
                  </label>
                  <textarea
                    id="feedback-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请描述你遇到的问题或建议，最多 500 字"
                    className="w-full resize-none rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-100 outline-none transition-colors placeholder:text-foreground-600 focus:border-primary-500/70"
                  />
                  <p className="mt-1 text-right text-[11px] text-foreground-600">最多 500 字</p>
                </div>

                <input
                  type="text"
                  name="contact_alt"
                  className="field-aux-note"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === "error" && formError && (
                  <p className="rounded-md border border-accent-600/50 bg-accent-500/10 px-3 py-2 text-xs leading-relaxed text-accent-300">
                    {formError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <i className="ri-send-plane-line text-base" />
                  {status === "submitting" ? "提交中..." : "提交反馈"}
                </button>
                <p className="text-[11px] leading-relaxed text-foreground-600">
                  提交即表示你同意我们通过该邮箱与你联系，我们不会将信息用于其他用途。
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      <section id="copyright" className="mt-10 scroll-mt-24">
        <div className="rounded-lg border border-background-200/70 bg-background-100/60 p-5 md:p-6">
          <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-100">
            <i className="ri-copyright-line text-primary-300" />
            版权声明
          </h2>
          <div className="mt-3 flex flex-col gap-2.5 text-sm leading-7 text-foreground-500">
            <p>
              本站页面中的影片名称、简介、导演与演员信息均为虚构示例内容，仅用于展示产品功能与页面结构，不指向任何真实影片或人物。
            </p>
            <p>
              站内海报与剧照为演示用生成图像。当前版本不提供真实影片播放服务，播放区域点击后仅展示功能说明。
            </p>
            <p>如需转载站内文字内容，请注明来源；如涉及内容或素材问题，欢迎通过上方邮箱与我们联系。</p>
          </div>
        </div>
      </section>
    </div>
  );
}